<template>
  <div class="task-kanban-card">
    <TirPmContextMenu
      ref="$contextMenu"
      min-width="24.25rem"
      maxHeight="max-content"
      :trigger-buttons="[TirPmContextMenuMouseButtonEnum.Right]"
    >
      <RouterLink
        class="task"
        ref="$cardContainer"
        :class="{
          'is-subtask': isSubtask,
          task__pointer: nonDraggableGroups,
        }"
        :to="computedRoute"
        @mousedown.middle.prevent
        @dragstart.prevent
      >
        <div v-if="isSubtask" class="task__subtask-line" />
        <div class="task__status-container" v-if="isView.Status">
          <div class="task__status">
            <div class="task__status-bg" :style="{ backgroundColor: task.Status.Color }" />
            <div class="task__status-dot" />
            <span class="task__status-text">{{ task.Status.Name }}</span>
          </div>
          <WeeekTag v-if="task.ImportService === TaskImportServiceEnum.Weeek" />
        </div>

        <div class="task__header">
          <span class="task__taskid">
            <HashtagIcon class="task-icon" />
            {{ task.Id }}
          </span>
          <WeeekTag v-if="!isView.Status && task.ImportService === TaskImportServiceEnum.Weeek" />
        </div>

        <div class="task__title-wrapper">
          <div class="task__title">{{ task?.Name }}</div>
        </div>

        <div class="task__controls" v-if="isView.Queue">
          <TirPmTooltipElement>
            <ListBulletIcon class="task__sort-icon task-icon" />
            <template #content>
              <div class="task__popover-title">Очередь</div>
            </template>
          </TirPmTooltipElement>

          <div class="task__queue">{{ task.Queues[0].Name }}</div>
        </div>
        <div
          class="task__user"
          v-if="task.Author && viewSettings.viewElements.includes(TaskViewElementEnum.Author)"
        >
          <TirPmTooltipElement>
            <UserIcon class="task__person-icon task-icon" />
            <template #content>
              <div class="task__popover-title">Автор</div>
            </template>
          </TirPmTooltipElement>

          <TirPmTooltipElement>
            <TirPmIconView
              :title="``"
              :size="24"
              isRounded
              :colorCode="task.Author.NoPhotoColor"
              :isBordered="false"
              :imageLoadUrl="task.Author.ImageUrl"
            >
              <template #mainSlot>
                {{ task.Author.FirstName[0] + task.Author.LastName[0] }}
              </template>
            </TirPmIconView>
            <template #content>
              <div class="task__popover-title">
                {{ task.Author.FirstName }} {{ task.Author.MiddleName }} {{ task.Author.LastName }}
              </div>
            </template>
          </TirPmTooltipElement>
        </div>

        <div class="task__performers" v-if="isView.Performers">
          <TirPmTooltipElement>
            <UserCircleIcon class="task__person-icon task-icon" />
            <template #content>
              <div class="task__popover-title">Исполнитель</div>
            </template>
          </TirPmTooltipElement>
          <div v-for="(person, idx) in performersCurrentUserFerst" class="task__persons" :key="idx">
            <TirPmTooltipElement>
              <TirPmIconView
                class="task__person"
                :key="person.Id"
                :size="24"
                isRounded
                :colorCode="person.NoPhotoColor"
                :isBordered="false"
                :imageLoadUrl="person.ImageUrl"
              >
                <template #mainSlot>
                  {{ person.FirstName[0] + person.LastName[0] }}
                </template>
              </TirPmIconView>
              <template #content>
                <div class="task__popover-title">
                  {{ person.FirstName }} {{ person.MiddleName }} {{ person.LastName }}
                </div>
              </template>
            </TirPmTooltipElement>
          </div>

          <TirPmIconView
            class="task__person"
            v-if="hiddenPerformers > 0"
            :size="24"
            isRounded
            :isBordered="false"
          >
            <template #mainSlot> +{{ hiddenPerformers }} </template>
          </TirPmIconView>
        </div>
        <div class="task__observers" v-if="isView.Observers">
          <TirPmTooltipElement>
            <EyeIcon class="task__person-icon task-icon" />
            <template #content>
              <div class="task__popover-title">Наблюдатель</div>
            </template>
          </TirPmTooltipElement>
          <div v-for="person in observersCurrentUserFerst" class="task__persons">
            <TirPmTooltipElement>
              <TirPmIconView
                class="task__person"
                :key="person.Id"
                :size="24"
                isRounded
                :colorCode="person.NoPhotoColor"
                :isBordered="false"
                :imageLoadUrl="person.ImageUrl"
              >
                <template #mainSlot>
                  {{ person.FirstName[0] + person.LastName[0] }}
                </template>
              </TirPmIconView>

              <template #content>
                <div class="task__popover-title">
                  {{ person.FirstName }} {{ person.MiddleName }} {{ person.LastName }}
                </div>
              </template>
            </TirPmTooltipElement>
            <TirPmIconView
              class="task__person"
              v-if="hiddenObservers > 0"
              :size="24"
              isRounded
              :isBordered="false"
            >
              <template #mainSlot> +{{ hiddenObservers }} </template>
            </TirPmIconView>
          </div>
        </div>

        <div class="task__priority" v-if="isView.Priority">
          <TirPmTooltipElement>
            <FlagIcon class="task-icon" :style="{ color: task.Priority.Color }" />
            <template #content>
              <div class="task__popover-title">Приоритет</div>
            </template>
          </TirPmTooltipElement>
          <span class="task__priority-text">{{ task.Priority.Name || '-' }}</span>
        </div>
        <div class="task__difficulty" v-if="isView.Difficulty">
          <TaskParameter
            preview
            :data="difficultyElement"
            :type="TaskParameterTypeEnum.Difficulty"
          />

          <span class="task__priority-text">{{ task.Difficulty.DifficultyName || '-' }}</span>
        </div>
        <div class="task__deadline" v-if="isView.Deadline">
          <TirPmTooltipElement>
            <CalendarDaysIcon class="task-icon" />

            <template #content>
              <div class="task__popover-title">Дедлайн</div>
            </template>
          </TirPmTooltipElement>

          {{ deadlineDateText }}
        </div>
        <div class="task__time-evaluation" v-if="isView.EstimateTime">
          <TirPmTooltipElement>
            <template #content>Оценка времени</template>
            <ClockIcon class="task-icon" />
          </TirPmTooltipElement>
          {{ formatTime(task.EstimateTime) }}
        </div>
        <div class="task__time-evaluation" v-if="isView.SpentTime">
          <TirPmTooltipElement>
            <template #content>Затраченное время</template>
            <TimeSpentIcon class="task-icon" />
          </TirPmTooltipElement>
          {{ formatTime(task.WorkTime) }}
        </div>

        <div class="task__tags" v-if="isView.Tags">
          <TirPmTooltipElement>
            <TagIcon class="task__tag-icon task-icon" />

            <template #content>
              <div class="task__popover-title">Тег</div>
            </template>
          </TirPmTooltipElement>

          <div class="task__tags-wrapper">
            <div
              class="task__tag"
              v-for="tag in shownTags"
              :style="{ borderColor: tag.Color }"
              :key="tag.Id"
            >
              <div class="task__tag-bg" :style="{ backgroundColor: tag.Color }" />
              <span class="task__tag-text" :style="{ color: tag.Color }">{{ tag.Name }}</span>
            </div>
          </div>
          <div v-if="hiddenTags > 0" class="data-total__inner-tags">+{{ hiddenTags }}</div>
        </div>
        <div class="task__files" v-if="isView.Files">
          <PaperClipIcon class="task-icon" />
          <div class="task__files-wrapper">
            <div class="task__file" v-for="file in showFiles" :key="file.Id">
              <span class="task__file-name">{{ file.FileName }}</span>
              <span class="task__file-ext">{{ file.FileExtension }}</span>
            </div>
          </div>
          <div v-if="hiddenFiles > 0" class="data-total__inner-tags">+{{ hiddenFiles }}</div>
        </div>

        <div class="task__date-create" v-if="isView.DateCreate">
          {{ dateCreateText }}
        </div>
        <div class="task__type" v-if="isView.Type">
          <TirPmTooltipElement>
            <RectangleStackIcon class="task-icon" />
            <template #content>
              <div class="task__popover-title">Тип</div>
            </template>
          </TirPmTooltipElement>

          {{ task.Type.Name }}
        </div>
        <div
          v-if="task.SubtasksCount && viewSettings.showNestedTasks"
          :class="['task__subtasks-total-info', { 'is-subtask': isSubtask }]"
          @mousedown.stop
          @mouseup.stop
          @touchstart.stop
          @click.stop.prevent="toggelOpenSubtasks"
        >
          <div class="task__subtasks-total">Вложенных задач: {{ task.SubtasksCount }}</div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.3285 2.08093C4.17668 2.08093 3.22827 2.99421 3.22827 4.13869C3.22827 5.01777 3.78881 5.76115 4.56982 6.05794L4.90122 9.59189C4.97472 10.3757 5.55578 11.0161 6.32947 11.187L10.1787 12.0374C10.2692 13.099 11.1789 13.9209 12.2716 13.9209C13.4234 13.9209 14.3718 13.0076 14.3718 11.8632C14.3718 10.7187 13.4234 9.80542 12.2716 9.80542C11.4855 9.80542 10.7965 10.2294 10.4364 10.8617L6.59435 10.0129C6.32595 9.95363 6.13545 9.73511 6.1116 9.48071L5.79874 6.14452C6.72742 5.93596 7.42872 5.12268 7.42872 4.13869C7.42872 2.99421 6.48031 2.08093 5.3285 2.08093ZM4.37896 4.13869C4.37896 3.63965 4.79598 3.22061 5.3285 3.22061C5.86102 3.22061 6.27803 3.63965 6.27803 4.13869C6.27803 4.63772 5.86102 5.05676 5.3285 5.05676C4.79598 5.05676 4.37896 4.63772 4.37896 4.13869ZM11.322 11.8632C11.322 11.3641 11.739 10.9451 12.2716 10.9451C12.8041 10.9451 13.2211 11.3641 13.2211 11.8632C13.2211 12.3622 12.8041 12.7812 12.2716 12.7812C11.739 12.7812 11.322 12.3622 11.322 11.8632Z"
              fill="white"
            />
          </svg>
        </div>
      </RouterLink>
      <template #content>
        <TirPmContextMenuButton @click="openTaskInDrawer(task.Id)">
          <div class="menu-item">
            <CurtainIcon class="menu-icon" />
            Открыть в шторке
          </div>
        </TirPmContextMenuButton>
        <RouterLink :to="computedRoute" target="_blank">
          <TirPmContextMenuButton>
            <div class="menu-item">
              <ArrowTopRightOnSquareIcon class="menu-icon" />
              Открыть в новой вкладке
            </div>
          </TirPmContextMenuButton>
        </RouterLink>
        <RouterLink :to="openSchema">
          <TirPmContextMenuButton>
            <div class="menu-item">
              <MapIcon class="menu-icon" />
              Посмотреть карту проекта
            </div>
          </TirPmContextMenuButton>
        </RouterLink>
        <TirPmContextMenuButton @click="copyNameAndLinkTask" class="task-card__menu-item">
          <ClipboardDocumentIcon class="menu-icon" />
          Скопировать название и ссылку
        </TirPmContextMenuButton>
        <PerformerListByOrder
          v-if="accessAddQueue"
          :taskInfo="task"
          @closePopover="$contextMenu.close()"
        />
        <TirPmContextMenuButton
          v-if="isProjectAdmin || isOwner"
          @click="openRemoveModal(task.Id, task.Name)"
        >
          <div class="menu-item">
            <TrashIcon class="menu-icon task__negative" />
            <span class="task__negative">Удалить тикет</span>
          </div>
        </TirPmContextMenuButton>
      </template>
    </TirPmContextMenu>
    <div
      v-if="task.Subtasks?.length && viewSettings.showNestedTasks && isOpenSubtasks"
      class="task-kanban-card__subtasks"
    >
      <TaskKanbanCard
        v-for="(subtask, idx) in task.Subtasks"
        :key="idx"
        :task="subtask"
        is-subtask
        :view-settings="viewSettings"
        :groupping-type="grouppingType"
        :project-id="projectId"
        :queue-id="queueId"
        :isProjectAdmin="isProjectAdmin"
        @open-in-drawer="emit('openInDrawer', $event)"
        @remove="openRemoveModal"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ComponentPublicInstance, PropType, computed, onMounted, ref, watch } from 'vue';
import { TaskAttachedFileModel, TaskModel, TaskTagModel } from '../models';
import { TaskViewSettings } from '../types';
import { TasksKanbanGrouppingTypeEnum, TaskViewElementEnum } from '../enums';
import {
  TirPmContextMenu,
  TirPmContextMenuButton,
  TirPmContextMenuMouseButtonEnum,
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
  TirPmTooltipElement,
} from 'tir-components';
import {
  ListBulletIcon,
  UserCircleIcon,
  TagIcon,
  EyeIcon,
  PaperClipIcon,
  ClockIcon,
  RectangleStackIcon,
  HashtagIcon,
  UserIcon,
  MapIcon,
  ShareIcon,
  TrashIcon,
  CurtainIcon,
  ClipboardDocumentIcon,
  CalendarDaysIcon,
  TimeSpentIcon,
} from 'tir-style-system/icons/outline';
import { ArrowTopRightOnSquareIcon, FlagIcon } from 'tir-style-system/icons/solid';
import { useResizeObserve } from '../composables';
import { getElementRect } from '../utils';
import { RouterLink, useRouter } from 'vue-router';
import { TaskParameter, TaskParameterModel, TaskParameterTypeEnum, WeeekTag } from '@/components';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { copyToClipboard } from '../utils';
import { TaskImportServiceEnum } from '@/enums';
import PerformerListByOrder from './PerformerListByOrder.vue';
import { TaskKanbanAdapterService } from '../adapter';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

const router = useRouter();
/**Адаптер */
const adapter = new TaskKanbanAdapterService();

const emit = defineEmits<{
  (e: 'loadSubtasks'): void;
  /**Событие для открытия задачи в шторке */
  (e: 'openInDrawer', taskId: number): void;
  /**Событие для раскрытия сайтбара */
  (e: 'toggle-submenu', queueId: string): void;
  /**Событие удаления задачи */
  (e: 'remove', id: number, name: string): void;
}>();

const props = defineProps({
  /**Данные задачи */
  task: {
    type: Object as PropType<TaskModel>,
    required: true,
  },
  /** Является ли задача вложенной */
  isSubtask: {
    type: Boolean,
    default: false,
  },
  /**Настройки отображения */
  viewSettings: {
    type: Object as PropType<TaskViewSettings>,
    default: (): TaskViewSettings => ({
      showNestedTasks: true,
      viewElements: Object.values(TaskViewElementEnum),
    }),
  },
  /**Группировка */
  grouppingType: {
    type: Number as PropType<TasksKanbanGrouppingTypeEnum>,
    required: true,
  },
  /**ID очереди */
  queueId: {
    type: String,
    required: true,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
  /**Пользователья является админом проекта */
  isProjectAdmin: { type: Boolean },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});

/**Ссылка на элемент контейнера карточки */
const $cardContainer = ref<ComponentPublicInstance>();

/**Ссылка на компонент контекстного меню */
const $contextMenu = ref<InstanceType<typeof TirPmContextMenu>>();
/**Является ли юзер овнером */
const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);
/**Элемент сложности */
const difficultyElement = new TaskParameterModel({
  Id: props.task.Difficulty.DifficultyId,
  Color: props.task.Difficulty.DifficultyColor,
});

/**Ширина карточки */
const { width } = useResizeObserve($cardContainer, 10, 256);

/**Получить видимые элементы */
const getShowItems = <T>(
  items: Array<T>,
  getWidthItem: ((item: T) => number) | number,
  containerWidth: number,
) => {
  const showItems = new Array<T>();
  let currentWidth = 0;

  for (const item of items) {
    const itemWidth = typeof getWidthItem === 'function' ? getWidthItem(item) : getWidthItem;
    if (currentWidth + itemWidth <= containerWidth) {
      currentWidth += itemWidth;
      showItems.push(item);
    } else {
      break;
    }
  }
  return showItems;
};

/**
 * Функция для сортировки юзеров где текущий юзер отобржается первым
 *
 * @param users Список юзеров
 */

const sortUsersByCurrentUser = (users) => {
  return users.sort((a, b) => {
    if (a.Id == CurrentUser.Id) return -1;
    if (b.Id == CurrentUser.Id) return 1;
  });
};

/** Показанные исполнители */
const shownPerformers = computed(() => getShowItems(props.task.Performers, 23, width.value - 60));
let performersCurrentUserFerst;

const shownPerformersFerstCurrentUser = () => {
  performersCurrentUserFerst = sortUsersByCurrentUser(shownPerformers.value);
};

/** Показанные наблюдатели */
const shownObservers = computed(() => getShowItems(props.task.Observers, 23, width.value - 60));
let observersCurrentUserFerst;

const shownObserversFerstCurrentUser = () => {
  observersCurrentUserFerst = sortUsersByCurrentUser(shownObservers.value);
};
/**Доступ к добавлению задачи в очередность исполнителя */
const accessAddQueue = computed(() => props.userAccess.CanCustomizeOrder || isOwner);

/** Скрытые исполнители */
const hiddenPerformers = computed(
  () => props.task.Performers.length - shownPerformers.value.length,
);
/** Скрытые наблюдатели */
const hiddenObservers = computed(() => props.task.Observers.length - shownObservers.value.length);
/**Не передвигаемые группировки */
const nonDraggableGroups = computed<boolean>(() => {
  return (
    props.grouppingType === TasksKanbanGrouppingTypeEnum.Performer ||
    props.grouppingType === TasksKanbanGrouppingTypeEnum.Tag
  );
});

/**Получить ширину тега */
const getTagWidth = (tag: TaskTagModel) => {
  const template = `<div style="font-size: 0.875rem; padding: 0.125rem 0.5rem;">${tag.Name}</div>`;
  return Math.min(getElementRect(template)?.width ?? 0, 112) + 4;
};

/** Показанные тэги */
const shownTags = computed(() => {
  return getShowItems(props.task.Tags, getTagWidth, width.value - 60);
});
/** Скрытые тэги */
const hiddenTags = computed(() => props.task.Tags.length - shownTags.value.length);

/** Получить ширину тега-файла */
const getFileTagWidth = (file: TaskAttachedFileModel) => {
  const template = `<div style="padding: 0.125rem 0.5rem; font-size: 0.75rem; display: flex; align-items: center; column-gap: 0.25rem; width: fit-content; white-space: nowrap;"><span>${file.FileName}</span><span>${file.FileExtension}</span></div>`;
  return Math.min(getElementRect(template)?.width ?? 0, 154) + 4;
};

/** Показанные файлы */
const showFiles = computed(() =>
  getShowItems(props.task.AttachedFiles, getFileTagWidth, width.value - 60),
);

/** Скрытые файлы */
const hiddenFiles = computed(() => props.task.AttachedFiles.length - showFiles.value.length);

const formatNumber = (num: number) => {
  return num.toString().length === 2 ? num.toString() : `0${num}`;
};

const formatDate = (date: Date) => {
  return `${formatNumber(date.getDate())}.${formatNumber(
    date.getMonth() + 1,
  )}.${date.getFullYear()} в ${formatNumber(date.getHours())}:${formatNumber(date.getMinutes())}`;
};

/**Текст с датой создания */
const dateCreateText = computed(() => {
  if (props.task.DateCreate) {
    return `Создано ${formatDate(props.task.DateCreate)}`;
  }
});

/**Текст с датой дедлайна */
const deadlineDateText = computed(() => {
  if (props.task.Deadline) {
    return formatDate(props.task.Deadline);
  }
});
/**Маршрут деталки задачи */
const computedRoute = computed(() => {
  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.projectId,
      queueId: props.queueId,
      taskId: props.task.Id,
    },
  });
  emit('toggle-submenu', props.queueId);
  return route;
});

const isView = computed(() => {
  const viewElements = props.viewSettings.viewElements;

  const settings = {
    Author: props.task.Author && viewElements.includes(TaskViewElementEnum.Author),
    Performers:
      shownPerformers.value.length && viewElements.includes(TaskViewElementEnum.Performers),
    Observers: shownObservers.value.length && viewElements.includes(TaskViewElementEnum.Observers),
    Priority: props.task.Priority && viewElements.includes(TaskViewElementEnum.Priority),
    Status:
      props.task.Status &&
      viewElements.includes(TaskViewElementEnum.Status) &&
      props.grouppingType !== TasksKanbanGrouppingTypeEnum.Status,
    Tags: shownTags.value.length && viewElements.includes(TaskViewElementEnum.Tags),
    Files: showFiles.value.length && viewElements.includes(TaskViewElementEnum.AttachedFiles),
    DateCreate: dateCreateText.value && viewElements.includes(TaskViewElementEnum.DateCreate),
    Type: props.task.Type?.Name && viewElements.includes(TaskViewElementEnum.Type),
    Deadline: deadlineDateText.value && viewElements.includes(TaskViewElementEnum.Deadline),
    EstimateTime: viewElements.includes(TaskViewElementEnum.EstimateTime),
    SpentTime: viewElements.includes(TaskViewElementEnum.WorkTime),
    Difficulty:
      props.task.Difficulty?.DifficultyId && viewElements.includes(TaskViewElementEnum.Difficulty),
    Queue: props.task?.Queues?.length && viewElements.includes(TaskViewElementEnum.Queue),
    MiddleBlock: false,
    FooterBlock: false,
  };

  settings.MiddleBlock =
    settings.Performers ||
    settings.Observers ||
    settings.Priority ||
    settings.Difficulty ||
    settings.Tags ||
    settings.Files;
  settings.FooterBlock = settings.DateCreate || settings.Type || settings.Deadline;

  return settings;
});

/**Открыти ли подзадачи */
const isOpenSubtasks = ref(false);

/**Открыть карту проекта */
const openSchema = computed(() => {
  return router.resolve({
    name: 'project-map',
    params: {
      taskId: props.task.Id,
      projectId: props.task.ProjectId,
    },
  });
});

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'team-tasks',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.projectId,
      queueId: props.queueId,
      taskId: props.task.Id,
    },
  });
  if (!route?.href) return;

  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + route.href;
  const textContent = `${props.task.Id} ${props.task.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    $contextMenu.value.close();
    addNotification('Название и ссылка тикета скопирована');
    console.log(9);
  }
};
/**Форматирование времени */
const formatTime = (time: number) => {
  if (!time) return '-';
  const hours = Math.floor(time);
  const minutes = Math.round((time % 1) * 100); // 0.46 -> 46
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**Переключить видимость подзадач */
const toggelOpenSubtasks = () => {
  if (props.isSubtask || props.task.IsLoadingSubtasks) return;
  isOpenSubtasks.value = !isOpenSubtasks.value;
  if (props.task.Subtasks?.length) return;
  emit('loadSubtasks');
};

const openTaskInDrawer = (taskId: number) => {
  $contextMenu.value?.close();
  emit('openInDrawer', taskId);
};

watch(
  [() => props.task.Performers, () => props.task.Observers],
  () => {
    shownPerformersFerstCurrentUser();
    shownObserversFerstCurrentUser();
  },
  { immediate: true },
);
/**Открыть окно удаления задачи */
const openRemoveModal = (id: number, name: string) => {
  $contextMenu.value?.close();
  emit('remove', id, name);
};
</script>

<style lang="scss" scoped>
.task-card {
  &__menu-item--add-to-queue {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
}
:deep(.tag__sign) {
  white-space: var(--whiteSpace);
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 0;
  padding-right: 0;
}

:deep(.tag__left-icon),
:deep(.tag__right-icon) {
  &:empty {
    display: none;
  }
}

.task {
  display: flex;
  min-width: 12.5rem;
  max-width: 19.25rem;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  background-color: var(--bg-base, #272d37);
  margin-bottom: 0.4rem;
  color: var(--text-primary);

  &__time-evaluation {
    display: flex;
    align-items: center;
    color: var(--text-secondary);
  }
  border: 0.063rem solid transparent;
  &:active {
    border-color: var(--neutral-translucent-20, rgba(39, 45, 55, 0.2));
  }

  &.is-subtask {
    margin-left: 1rem;
  }

  &__popover-title {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
    font-size: 0.75rem;
  }
  &__negative {
    color: var(--text-negative);
  }
  &__pointer {
    cursor: pointer;
  }
}

.is-subtask {
  max-width: 18.5rem;
}

.task__subtask-line {
  border-radius: 0.25rem;
  height: 0.188rem;
  background-color: #3a7cc2;
}

.task__title {
  font-size: 1rem;
  line-height: 1.25rem;
  color: #272d37;
  font-weight: 400;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.task__sort-icon {
  margin-right: 0.25rem;
}

.task__controls {
  display: flex;
}

.task__queue {
  flex: 1 1 100%;
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #686c73;
}

.task__line {
  width: 100%;
  height: 0.063rem;
  background-color: #bfc0c3;
}

.task-icon {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  color: var(--text-secondary, #686c73);
  margin-right: 0.5rem;
}

.task__performers,
.task__observers {
  display: flex;
  align-items: center;
}

.task__person-icon {
  margin-right: 0.5rem;
}

.task__persons {
  display: flex;
}

.task__person:not(:first-of-type) {
  margin-left: -0.25rem;
}

.data-total__inner,
.task__person-inner {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.75rem;
}

.data-total__inner {
  color: #272d37;
  font-weight: 500;
  background-color: #f8f6f9;
}

.task__tags {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.task__tags-wrapper {
  display: flex;
  align-items: center;
}

.task__tag-icon {
  margin-right: 0.5rem;
}

.task__tag {
  position: relative;
  overflow: hidden;
  padding: 0.125rem 0.5rem;
  font-size: 0.688rem;
  border-style: solid;
  border-width: 0.063rem;
  border-radius: 0.25rem;
  margin-right: 0.125rem;

  &-bg {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    opacity: 0.1;
  }
  &-text {
    position: relative;
    z-index: 1;
    line-height: 1rem;
    font-weight: 400;
  }
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.menu-icon {
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
}

.data-total__inner-tags {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  background-color: var(--neutral-translucent-10, #272d371a);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #272d37;
  font-weight: 500;
  margin-left: 0.125rem;
}

.task__subtasks-total-info {
  border-radius: 0.25rem;
  background-color: var(--text-info, #2a77ef);
  text-align: right;
  padding: 0.125rem;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 400;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0.375rem;
  cursor: pointer;

  &.is-subtask {
    background-color: var(--status-attention, #ea8313);
  }
}

.task__subtasks-total {
  margin-right: 0.25rem;
}

.task__priority {
  display: flex;
  align-items: center;
  &-text {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    color: var(--text-secondary);
  }
}

.task__difficulty {
  display: flex;
  align-items: center;
}

.task__taskid {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: var(--text-primary);
}

.task__user {
  display: flex;
  align-items: center;
}

.task__files {
  display: flex;
  align-items: center;
}

.task__files-wrapper {
  display: flex;
  align-items: center;
  min-height: 1.5rem;
}

.task__file {
  background-color: var(--bg-info);
  color: var(--text-info);
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  display: flex;
  align-items: center;
  max-width: 9.625rem;
  white-space: nowrap;

  &-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.task__date-create {
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 400;
}

.task__footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.task__type {
  display: flex;
  align-items: center;
  color: var(--text-primary, #272d37);
  font-size: 0.875rem;
  font-style: normal;
  max-width: 8.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task__deadline {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
}

.task__title-wrapper {
  display: flex;
}
.task__more-info-icon {
  flex-shrink: 0;
}

.task__header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  justify-content: space-between;
}

.task__status {
  position: relative;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  overflow: hidden;

  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: fit-content;
}

.task__status-bg {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
}

.task__status-text {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1rem;
  letter-spacing: 0.063rem;
  color: var(--text-inverted-primary);
  z-index: 1;
}

.task__status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: var(--text-inverted-primary);
  z-index: 1;
}

.task__status-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>

<style lang="scss">
.tasks-kanban {
  .task-kanban-card {
    &__animation-enter-active,
    &__animation-leave-active {
      transition: 0.3s;
      opacity: 1;
    }
    &__animation-enter-from,
    &__animation-leave-to {
      transition: 0.3s;
      opacity: 0;
    }
  }
}
.task-kanban-card {
  .task {
    box-shadow: 0rem 0.25rem 0.25rem 0rem rgba(0, 0, 0, 0.09);

    &:hover {
      box-shadow: 0rem 0.35rem 0.788rem 0.125rem rgba(0, 0, 0, 0.25);
    }
  }
}
</style>
